import { useState, useEffect } from 'react';
import { Icon } from '../../icons';
import { Badge, Alert, Card, SectionHeader, FormRow, Field, Btn } from '../../components/ui';
import { getProfile, updateProfile, updatePassword, updateEmail, getRecentActivities } from '../../api/profile';
import { getErrorMessage, getFieldError } from '../../utils/apiUtils';
import { toastSuccess, toastError } from '../../utils/toast';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function MyProfileScreen({ onNav }) {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.settings?.[key] || translations[language]?.admin?.[key] || translations.en?.settings?.[key] || translations.en?.admin?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hovAvatar, setHovAvatar] = useState(false);

  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);

  const [form, setForm] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const [passForm, setPassForm] = useState({ current_password: "", password: "", password_confirmation: "" });
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  const [apiErrors, setApiErrors] = useState(null);
  const [passwordErrors, setPasswordErrors] = useState(null);
  const [emailErrors, setEmailErrors] = useState(null);

  const fp = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (apiErrors?.errors?.[k]) setApiErrors(prev => ({ ...prev, errors: { ...prev.errors, [k]: null } }));
  };

  const fpp = k => e => {
    setPassForm(f => ({ ...f, [k]: e.target.value }));
    if (passwordErrors?.errors?.[k]) setPasswordErrors(prev => ({ ...prev, errors: { ...prev.errors, [k]: null } }));
  };

  const fpe = k => e => {
    setEmailForm(f => ({ ...f, [k]: e.target.value }));
    if (emailErrors?.errors?.[k]) setEmailErrors(prev => ({ ...prev, errors: { ...prev.errors, [k]: null } }));
  };

  const fetchProfileData = async () => {
    try {
      const res = await getProfile();
      if (res.success && res.data) {
        setProfile(res.data);
        setForm({
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          address: res.data.address || "",
          bio: res.data.bio || "",
          linkedin_url: res.data.linkedin_url || "",
          emergency_contact_name: res.data.emergency_contact_name || "",
          emergency_contact_phone: res.data.emergency_contact_phone || "",
        });
      }
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_fetch_profile_failed")));
      toastError(t("toast_fetch_profile_failed"));
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await getRecentActivities();
      if (res.success && res.data) {
        setActivities(res.data);
      }
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_fetch_activities_failed")));
    }
  };

  const initData = async () => {
    setLoading(true);
    await Promise.all([fetchProfileData(), fetchActivities()]);
    setLoading(false);
  };

  useEffect(() => {
    initData();
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const save = async () => {
    // Client-side Validation Checks
    const errors = {};
    if (!form.name || form.name.trim() === '') {
      errors.name = [t("val_name_required")];
    }
    if (!form.phone || form.phone.trim() === '') {
      errors.phone = [t("val_phone_required")];
    } else if (!/^([0-9\s\-\+\(\)]*)$/.test(form.phone)) {
      errors.phone = [t("val_phone_format")];
    }
    if (!form.address || form.address.trim() === '') {
      errors.address = [t("val_address_required")];
    }

    if (form.emergency_contact_phone && form.emergency_contact_phone.trim() !== '') {
      if (!/^([0-9\s\-\+\(\)]*)$/.test(form.emergency_contact_phone)) {
        errors.emergency_contact_phone = [t("val_emergency_phone_format")];
      }
    }

    if (form.linkedin_url && form.linkedin_url.trim() !== '') {
      try {
        new URL(form.linkedin_url);
      } catch (_) {
        errors.linkedin_url = [t("val_linkedin_format")];
      }
    }

    if (Object.keys(errors).length > 0) {
      setApiErrors({
        message: t("val_failed"),
        errors: errors
      });
      toastError(t("toast_fix_validation"));
      return;
    }

    setIsSaving(true);
    setApiErrors(null);
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('name', form.name);
      formData.append('phone', form.phone || '');
      formData.append('bio', form.bio || '');
      formData.append('address', form.address || '');
      formData.append('linkedin_url', form.linkedin_url || '');
      formData.append('emergency_contact_name', form.emergency_contact_name || '');
      formData.append('emergency_contact_phone', form.emergency_contact_phone || '');

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      await updateProfile(formData);

      // Reload profile
      await fetchProfileData();
      setAvatarFile(null);
      setAvatarPreview(null);
      setEditMode(false);
      setSaved(true);
      toastSuccess(t("toast_profile_updated"));

      // Dispatch custom event to notify Sidebar/Topbar
      window.dispatchEvent(new CustomEvent('profile-updated'));

      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setApiErrors(err);
      toastError(getErrorMessage(err, t("toast_update_profile_failed")));
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = () => {
    setForm({
      name: profile.name || "",
      email: profile.email || "",
      phone: profile.phone || "",
      address: profile.address || "",
      bio: profile.bio || "",
      linkedin_url: profile.linkedin_url || "",
      emergency_contact_name: profile.emergency_contact_name || "",
      emergency_contact_phone: profile.emergency_contact_phone || "",
    });
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditMode(false);
    setApiErrors(null);
  };

  const savePassword = async () => {
    setPasswordSaving(true);
    setPasswordErrors(null);
    try {
      await updatePassword(passForm);
      setPassForm({ current_password: "", password: "", password_confirmation: "" });
      toastSuccess(t("toast_password_changed"));
    } catch (err) {
      setPasswordErrors(err);
      toastError(getErrorMessage(err, t("toast_change_password_failed")));
    } finally {
      setPasswordSaving(false);
    }
  };

  const saveEmail = async () => {
    setEmailSaving(true);
    setEmailErrors(null);
    try {
      await updateEmail(emailForm);
      setEmailForm({ email: "", password: "" });
      toastSuccess("Email updated successfully.");
      await fetchProfileData(); // refresh email
    } catch (err) {
      setEmailErrors(err);
      toastError(getErrorMessage(err, "Failed to update email."));
    } finally {
      setEmailSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);

    if (diffMins < 1) return t('text_just_now');
    if (diffMins < 60) return t(diffMins > 1 ? 'text_mins_ago' : 'text_min_ago', { mins: diffMins });
    if (diffHrs < 24) return t(diffHrs > 1 ? 'text_hrs_ago' : 'text_hr_ago', { hrs: diffHrs });

    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return t('text_yesterday');
    if (diffDays < 7) return t('text_days_ago', { days: diffDays });

    return formatDateByPreference(date);
  };

  const getActivityIcon = (module) => {
    const map = {
      auth: { icon: "lock", color: "#10b981" },
      users: { icon: "users", color: "#4f46e5" },
      finance: { icon: "cash", color: "#8b5cf6" },
      audit: { icon: "activity", color: "#f59e0b" },
      files: { icon: "document", color: "#06b6d4" }
    };
    return map[module] || { icon: "check", color: "#64748b" };
  };

  if (loading || !profile) {
    return (
      <div className="fade-up space-y-5 max-w-5xl animate-pulse">
        <Card noPad>
          <div className="h-28 rounded-t-xl2 bg-slate-200 dark:bg-slate-800" />
          <div className="px-6 pb-6 pt-10 space-y-4">
            <div className="w-24 h-24 rounded-2xl bg-slate-300 dark:bg-slate-700 -mt-16 border-4 border-white dark:border-slate-900" />
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4" />
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
            <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded w-full" />
          </div>
        </Card>
      </div>
    );
  }


  const translateAction = (text) => {
    if (!text) return text;

    let match = text.match(/^(Created|Updated|Deleted) ([a-zA-Z0-9_]+) #(\d+)$/i);
    if (match) {
      const actionKey = `action_${match[1].toLowerCase()}`;
      const modelKey = `model_${match[2].toLowerCase()}`;

      const actionTranslated = t(actionKey) !== actionKey ? t(actionKey) : match[1];
      const modelTranslated = t(modelKey) !== modelKey ? t(modelKey) : match[2];

      return t("log_model_action", {
        action: actionTranslated,
        model: modelTranslated,
        id: match[3]
      });
    }

    match = text.match(/^Ran payroll for (\d+) staff for (.+)$/i);
    if (match) {
      return t("log_ran_payroll", { count: match[1], month: match[2] });
    }

    const translated = t(text);
    return translated !== text ? translated : text;
  };

  const quickStats = [
    { label: t("stat_modules_managed"), value: profile.modules_managed_count ?? 0, icon: "dashboard", color: "#4f46e5" },
    { label: t("stat_days_active"), value: (profile.days_active ?? 1).toLocaleString(), icon: "calendar", color: "#10b981" },
    { label: t("stat_actions_logged"), value: profile.actions_logged_count ?? 0, icon: "activity", color: "#f59e0b" },
    { label: t("stat_last_login"), value: profile.last_login_at ? formatTimeAgo(profile.last_login_at) : "Just now", icon: "check", color: "#06b6d4" },
  ];

  return (
    <div className="fade-up space-y-5 max-w-5xl">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-ink dark:text-slate-100">{t("title_my_profile")}</h1>
        <button onClick={() => onNav('create-society')} className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors">
          <Icon name="plus" size={14} /> Add Society
        </button>
      </div>
      {saved && <Alert type="success" onClose={() => setSaved(false)}>{t("toast_profile_saved")}</Alert>}
      <Card noPad>
        <div className="h-28 rounded-t-xl2 relative overflow-hidden bg-[linear-gradient(135deg,#0f172a_0%,#1e1b4b_60%,#312e81_100%)] dark:bg-[linear-gradient(135deg,#0b1120_0%,#111827_60%,#1e1b4b_100%)]">
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="absolute top-3 right-4 flex gap-2">
            {!editMode
              ? <button onClick={() => setEditMode(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.8)", fontSize: 11, fontWeight: 600, cursor: "pointer", backdropFilter: "blur(4px)", fontFamily: "inherit" }} className="dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100">
                <Icon name="edit" size={12} color="rgba(255,255,255,0.8)" /> {t("btn_edit_profile")}
              </button>
              : <div style={{ display: "flex", gap: 8 }}>
                <button onClick={save} disabled={isSaving} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: isSaving ? "#818cf8" : "#4f46e5", color: "#fff", fontSize: 11, fontWeight: 700, cursor: isSaving ? "not-allowed" : "pointer", opacity: isSaving ? 0.8 : 1, fontFamily: "inherit", transition: "all 0.2s ease" }} className="dark:bg-indigo-600">
                  {isSaving ? t("btn_saving") : t("btn_save_changes")}
                </button>
                <button onClick={cancel} disabled={isSaving} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600, cursor: isSaving ? "not-allowed" : "pointer", opacity: isSaving ? 0.6 : 1, fontFamily: "inherit" }} className="dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-300">{t("btn_cancel")}</button>
              </div>
            }
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5 -mt-12 mb-5 relative z-10">
            <div className={`relative flex-shrink-0 self-start sm:self-auto ${editMode ? 'cursor-pointer' : ''}`} onMouseEnter={() => setHovAvatar(true)} onMouseLeave={() => setHovAvatar(false)} onClick={() => editMode && document.getElementById('avatar-upload').click()}>
              {avatarPreview || profile.avatar ? (
                <img src={avatarPreview || profile.avatar} className="w-24 h-24 rounded-2xl object-cover bg-white dark:bg-slate-900" style={{ border: "4px solid white", boxShadow: "0 4px 16px rgba(79,70,229,0.25)" }} />
              ) : (
                <div className="w-24 h-24 rounded-2xl gradient-primary flex items-center justify-center text-white font-black text-2xl" style={{ border: "4px solid white", boxShadow: "0 4px 16px rgba(79,70,229,0.25)" }}>
                  {getInitials(profile.name)}
                </div>
              )}
              {editMode && hovAvatar && (
                <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background: "rgba(15,23,42,0.65)" }}>
                  <div className="text-center">
                    <Icon name="camera" size={18} color="white" />
                    <div style={{ color: "white", fontSize: 9, fontWeight: 600, marginTop: 2 }}>{t("btn_change")}</div>
                  </div>
                </div>
              )}
              {editMode && (
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              )}
            </div>
            <div className="pb-1 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1">
                <div className="text-lg font-black text-ink dark:text-slate-100 truncate">{profile.name}</div>
                <div className="text-sm text-muted dark:text-slate-400 pt-1.5">({profile.role})</div>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <Icon name="calendar" size={11} className="text-muted dark:text-slate-400" />
                <span className="text-xs text-muted dark:text-slate-400">{t("text_member_since")} {formatDateByPreference(profile.member_since)}</span>
              </div>
            </div>
            <div className="pb-1 flex flex-wrap gap-2">
              <Badge color="green" dot>{t("status_active")}</Badge>
              <Badge color="blue">{profile.role}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {quickStats.map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: s.color + "18" }}>
                  <Icon name={s.icon} size={14} color={s.color} />
                </div>
                <div>
                  <div className="text-base font-black text-ink dark:text-slate-100 leading-none">{s.value}</div>
                  <div className="text-sm text-muted dark:text-slate-400 mt-0.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
          {!editMode ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-bold text-muted dark:text-slate-400 uppercase tracking-wider mb-3">{t("title_contact_info")}</div>
                {[["mail", t("label_email"), profile.email], ["phone_call", t("label_phone"), profile.phone || t("text_not_set")], ["map_pin", t("label_address"), profile.address || t("text_not_set")]].map(([ic, lbl, val]) => (
                  <div key={lbl} className="flex gap-3 mb-3.5">
                    <div className="w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-800 border border-border dark:border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name={ic} size={13} className="text-muted dark:text-slate-400" /></div>
                    <div>
                      <div className="text-sm text-muted dark:text-slate-400 uppercase tracking-wide font-semibold mb-0.5">{lbl}</div>
                      <div className="text-sm text-ink dark:text-slate-100">{val}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-bold text-muted dark:text-slate-400 uppercase tracking-wider mb-3">{t("title_additional_details")}</div>
                {[
                  ["users", t("label_emergency_contact"), profile.emergency_contact_name ? `${profile.emergency_contact_name} - ${profile.emergency_contact_phone || ''}` : t("text_not_set")],
                  ["link", t("label_linkedin"), profile.linkedin_url || t("text_not_set")]
                ].map(([ic, lbl, val]) => (
                  <div key={lbl} className="flex gap-3 mb-3.5">
                    <div className="w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-800 border border-border dark:border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name={ic} size={13} className="text-muted dark:text-slate-400" /></div>
                    <div>
                      <div className="text-sm text-muted dark:text-slate-400 uppercase tracking-wide font-semibold mb-0.5">{lbl}</div>
                      <div className="text-sm text-ink dark:text-slate-100">{val}</div>
                    </div>
                  </div>
                ))}
                <div className="text-xs font-bold text-muted dark:text-slate-400 uppercase tracking-wider mt-4 mb-2">{t("label_bio")}</div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{profile.bio || t("text_no_bio")}</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs font-bold text-muted dark:text-slate-400 uppercase tracking-wider mb-3">{t("title_edit_profile_info")}</div>
              <FormRow cols={2}>
                <Field label={t("label_full_name")} value={form.name} onChange={fp("name")} error={getFieldError(apiErrors, "name")} required />
                <Field label={t("label_phone")} value={form.phone} onChange={fp("phone")} error={getFieldError(apiErrors, "phone")} required />
              </FormRow>
              <FormRow cols={2}>
                <Field label={t("label_email_readonly")} type="email" value={form.email} disabled required />
                <Field label={t("label_designation_readonly")} value={profile.role} disabled required />
              </FormRow>
              <FormRow>
                <Field label={t("label_address")} value={form.address} onChange={fp("address")} error={getFieldError(apiErrors, "address")} required />
              </FormRow>
              <FormRow cols={2}>
                <Field label={t("label_emergency_contact_name")} value={form.emergency_contact_name} onChange={fp("emergency_contact_name")} error={getFieldError(apiErrors, "emergency_contact_name")} />
                <Field label={t("label_emergency_contact_phone")} value={form.emergency_contact_phone} onChange={fp("emergency_contact_phone")} error={getFieldError(apiErrors, "emergency_contact_phone")} />
              </FormRow>
              <FormRow>
                <Field label={t("label_linkedin_url")} value={form.linkedin_url} onChange={fp("linkedin_url")} error={getFieldError(apiErrors, "linkedin_url")} placeholder="https://linkedin.com/in/…" />
              </FormRow>
              <FormRow>
                <Field label={t("label_bio")} type="textarea" rows={3} value={form.bio} onChange={fp("bio")} error={getFieldError(apiErrors, "bio")} />
              </FormRow>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <SectionHeader title={t("title_recent_activity")} subtitle={t("subtitle_recent_activity")} />
        <div className="space-y-0.5">
          {activities.length > 0 ? (
            activities.map((a, i) => {
              const iconMeta = getActivityIcon(a.module);
              return (
                <div key={i} className="flex items-center gap-3 py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconMeta.color + "18" }}>
                    <Icon name={iconMeta.icon} size={13} color={iconMeta.color} />
                  </div>
                  <div className="flex-1 text-sm text-ink dark:text-slate-100">{translateAction(a.description)}</div>
                  <span className="text-xs text-muted dark:text-slate-400 flex-shrink-0">{formatTimeAgo(a.created_at)}</span>
                </div>
              );
            })
          ) : (
            <div className="text-sm text-muted dark:text-slate-400 py-4 text-center">{t("text_no_recent_activities")}</div>
          )}
        </div>
      </Card>

      <Card>
        <SectionHeader title={t("title_change_password")} subtitle={t("subtitle_change_password")} />
        <div className="max-w-md">
          <FormRow>
            <Field label={t("label_current_password")} type="password" value={passForm.current_password} onChange={fpp("current_password")} placeholder={t("placeholder_current_password")} error={getFieldError(passwordErrors, "current_password")} />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_new_password")} type="password" value={passForm.password} onChange={fpp("password")} placeholder={t("placeholder_new_password")} error={getFieldError(passwordErrors, "password")} />
            <Field label={t("label_confirm_new_password")} type="password" value={passForm.password_confirmation} onChange={fpp("password_confirmation")} placeholder={t("placeholder_confirm_new_password")} />
          </FormRow>
          <Btn sm variant="primary" icon="lock" onClick={savePassword} loading={passwordSaving}>{t("btn_update_password")}</Btn>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Change Email" subtitle="Update your account email address securely" />
        <div className="max-w-md">
          <FormRow>
            <Field label="New Email Address" type="email" value={emailForm.email} onChange={fpe("email")} placeholder="new@example.com" error={getFieldError(emailErrors, "email")} />
          </FormRow>
          <FormRow>
            <Field label="Current Password" type="password" value={emailForm.password} onChange={fpe("password")} placeholder="Enter your current password to confirm" error={getFieldError(emailErrors, "password")} />
          </FormRow>
          <Btn sm variant="primary" icon="mail" onClick={saveEmail} loading={emailSaving}>Update Email</Btn>
        </div>
      </Card>
    </div>
  );
}
